import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { HeroContent } from '../components/landing/HeroContent';
import { Footer } from '../components/landing/Footer';
import { Mail, MapPin } from 'lucide-react';
import { SkewedLinesBackground } from '../components/backgrounds/SkewedLinesBackground';
import { contactTranslations } from '../i18n/translations/pages';

const ContactPage: React.FC = () => {
  const { locale } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  // Get translations for current language, fallback to English if not available
  const translations = contactTranslations[locale as keyof typeof contactTranslations] || contactTranslations.en;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900" ref={parentRef}>
      {/* Hero Section with Navigation */}
      <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden bg-white dark:bg-neutral-950" ref={containerRef}>
        {/* Skewed Lines Background */}
        <SkewedLinesBackground />
        
        {/* Navbar */}
        <HeroContent containerRef={containerRef} parentRef={parentRef} showHeroProps={false} />
        
        {/* Contact Title */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-center text-gray-900 dark:text-white"
            >
              {translations.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 max-w-2xl mx-auto text-center text-base text-neutral-800 dark:text-neutral-200"
            >
              {translations.subtitle}
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Contact Content */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="max-w-lg mx-auto md:max-w-none md:grid md:grid-cols-2 md:gap-8">
          <div>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-extrabold text-gray-900 dark:text-white sm:text-3xl"
            >
              {translations.getInTouch}
            </motion.h2>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-9"
            >
              <div className="flex items-center mt-6">
                <div className="flex-shrink-0">
                  <Mail className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-3 text-base text-gray-500 dark:text-gray-300">
                  <p>{translations.email}</p>
                  <p className="mt-1">{translations.emailDescription}</p>
                </div>
              </div>
              <div className="flex items-center mt-6">
                <div className="flex-shrink-0">
                  <MapPin className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-3 text-base text-gray-500 dark:text-gray-300">
                  <p>{translations.location}</p>
                  <p className="mt-1">{translations.address}</p>
                </div>
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-12 sm:mt-16 md:mt-0"
          >
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white sm:text-3xl">
              {translations.joinCommunity}
            </h2>
            <p className="mt-3 text-lg text-gray-500 dark:text-gray-300">
              {translations.communityDescription}
            </p>
            
            <div className="mt-9 grid grid-cols-1 gap-6">
              <motion.a 
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                href="https://x.com/sanderbz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center p-5 bg-white dark:bg-neutral-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
              >
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-gray-900 dark:text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-base font-medium text-gray-900 dark:text-white">{translations.socialLinks.twitter.username}</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{translations.socialLinks.twitter.description}</p>
                </div>
              </motion.a>
              
              <motion.a 
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                href="https://www.linkedin.com/in/sandervanmeggelen/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center p-5 bg-white dark:bg-neutral-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
              >
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-[#0A66C2]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-base font-medium text-gray-900 dark:text-white">{translations.socialLinks.linkedin.name}</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{translations.socialLinks.linkedin.description}</p>
                </div>
              </motion.a>
              
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ContactPage; 